import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { forwardRef, memo, type ReactNode } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const dropdownMenuItem = tailwindVariants({
  base: 'group/dropdown-menu-item flex min-h-[32px] shrink-0 cursor-pointer radix-disabled:cursor-not-allowed items-center rounded-4 radix-disabled:bg-transparent radix-highlighted:bg-gray-4 px-1.5 text-inherit radix-disabled:opacity-50 outline-none hover:bg-gray-4',
  compoundVariants: [
    {
      className: 'h-auto',
      description: true,
      size: 'small',
    },
    {
      className: 'h-auto',
      description: true,
      size: 'large',
    },
  ],
  defaultVariants: {
    description: false,
    size: 'small',
  },
  variants: {
    description: {
      false: 'items-center py-0',
      true: 'items-start py-1.5',
    },
    size: {
      large: 'min-h-[36px] text-16',
      small: 'min-h-[32px] text-14',
    },
  },
});

export type DropdownMenuItemVariant = VariantProps<typeof dropdownMenuItem>;

type DescriptionAffixOffsetClassParams = {
  hasDescription: boolean;
  isSmall: boolean;
};

const getDescriptionAffixOffsetClass = ({ hasDescription, isSmall }: DescriptionAffixOffsetClassParams) => {
  if (!hasDescription) {
    return 'mt-0';
  }

  if (isSmall) {
    return 'mt-0.5';
  }

  return 'mt-1';
};

export type DropdownMenuItemProps = Omit<RadixDropdownMenu.DropdownMenuItemProps, 'prefix' | 'size'> &
  Omit<DropdownMenuItemVariant, 'description'> & {
    description?: ReactNode;
    label?: ReactNode;
    truncate?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
    shortcut?: string[];
  };

export const DropdownMenuItem = memo(
  forwardRef<HTMLDivElement, DropdownMenuItemProps>(
    (
      {
        children,
        className,
        description,
        label,
        prefix,
        shortcut,
        size = 'small',
        suffix,
        truncate = true,
        ...restOfProps
      },
      ref,
    ) => {
      const hasDescription = !!description;
      const isSmall = size === 'small';

      return (
        <RadixDropdownMenu.Item
          className={dropdownMenuItem({ className, description: !!description, size })}
          data-testid="DROPDOWN_MENU_ITEM"
          ref={ref}
          {...restOfProps}
        >
          {children ? (
            children
          ) : (
            <>
              {prefix ? (
                <div
                  className={classNames(
                    'mr-2 flex shrink-0 items-center',
                    getDescriptionAffixOffsetClass({ hasDescription, isSmall }),
                  )}
                >
                  {prefix}
                </div>
              ) : null}

              <div className={classNames('flex flex-col', truncate ? 'min-w-0 grow' : 'flex-shrink-0')}>
                <span
                  className={classNames(truncate ? 'truncate' : 'whitespace-nowrap', isSmall ? 'text-14' : 'text-16')}
                >
                  {label}
                </span>
                {hasDescription ? (
                  <span className={classNames('text-gray-10', isSmall ? 'text-12' : 'text-14')}>{description}</span>
                ) : null}
              </div>

              {suffix ? (
                <div
                  className={classNames(
                    'ml-2 flex shrink-0 items-center',
                    getDescriptionAffixOffsetClass({ hasDescription, isSmall }),
                  )}
                >
                  {suffix}
                </div>
              ) : null}
              {shortcut ? (
                <div className="ml-2 flex shrink-0 items-center">
                  {shortcut.map((key, index) => (
                    <kbd
                      className="mr-0.5 flex size-4 items-center justify-center rounded-4 bg-gray-3 text-10 text-gray-8 uppercase last:mr-0"
                      key={index}
                    >
                      {key}
                    </kbd>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </RadixDropdownMenu.Item>
      );
    },
  ),
);

DropdownMenuItem.displayName = 'DropdownMenuItem';
